import { createSignal } from "solid-js";
import Editor from "~/js/editor";
import type { Coordinates, EntitySize } from "~/solidJs/shared/helpers/types/ISizeble";
import type { TooltipState } from "./model";

const SHOW_DELAY_MS = 600;

// Global signal for tooltip state
const [globalState, setGlobalState] = createSignal<TooltipState>({
	isOpen: false,
	position: { left: 0, top: 0, height: 0, width: 0 },
	tooltipData: null
});

// Global function to show tooltip
export const showOverlayTooltip = (text: string, position: Coordinates & EntitySize) => {
	setGlobalState({
		isOpen: true,
		position,
		tooltipData: {
			text
		}
	});
};

// Global function to close tooltip
export const closeOverlayTooltip = () => {
	setGlobalState({
		isOpen: false,
		position: { left: 0, top: 0, height: 0, width: 0 },
		tooltipData: null
	});
};

// Helper function to attach tooltip to an element
export const attachOverlayTooltip = (targetElement: HTMLElement, text: string) => {
	let timeout: ReturnType<typeof setTimeout> | undefined;

	const mouseEnterListener = () => {
		timeout = setTimeout(() => {
			const rect = targetElement.getBoundingClientRect();

			const graphPos = Editor.context.graph.navigation.clientToGraphSpace(
				rect.left,
				rect.top
			);
			const zoom = Editor.context.graph.navigation.getZoomScale();
			const position = {
				left: graphPos.x,
				top: graphPos.y,
				width: rect.width / zoom,
				height: rect.height / zoom
			};
			showOverlayTooltip(text, position);
		}, SHOW_DELAY_MS);
	};

	const clearTooltip = () => {
		clearTimeout(timeout);
		closeOverlayTooltip();
	};

	const mouseLeaveListener = () => {
		clearTooltip();
	};

	const mouseUpListener = () => {
		clearTooltip();
	};

	targetElement.addEventListener("mouseenter", mouseEnterListener);
	targetElement.addEventListener("mouseleave", mouseLeaveListener);
	targetElement.addEventListener("mouseup", mouseUpListener);

	// Return cleanup function
	return () => {
		clearTimeout(timeout);
		targetElement.removeEventListener("mouseenter", mouseEnterListener);
		targetElement.removeEventListener("mouseleave", mouseLeaveListener);
		targetElement.removeEventListener("mouseup", mouseUpListener);
		closeOverlayTooltip();
	};
};

export const useOverlayTooltip = () => {
	return {
		isOpen: () => globalState().isOpen,
		setIsOpen: (value: boolean) => {
			if (!value) {
				closeOverlayTooltip();
			} else {
				const currentState = globalState();
				setGlobalState({
					...currentState,
					isOpen: true
				});
			}
		},
		position: () => globalState().position,
		tooltipData: () => globalState().tooltipData
	};
};
